// Minimalist Sign In / Sign Up panel rendered into a lightweight overlay.
// Anonymous-first: this only opens when the user explicitly chooses to.
import { login, signup, logout, getState, subscribe, requestPasswordReset } from "../lib/auth.js";
import { escapeHtml } from "./helpers.js";

let overlayEl = null;

function close() {
  overlayEl?.remove();
  overlayEl = null;
}

export function openAuthPanel(initialMode = "signin") {
  close();
  let mode = initialMode;

  overlayEl = document.createElement("div");
  overlayEl.className =
    "fixed inset-0 z-50 flex items-center justify-center bg-sage-900/30 backdrop-blur-sm p-4";
  overlayEl.addEventListener("click", (e) => {
    if (e.target === overlayEl) close();
  });

  function render() {
    if (mode === "reset") {
      renderReset();
      return;
    }
    const isSignup = mode === "signup";
    overlayEl.innerHTML = `
      <div class="w-full max-w-sm bg-white rounded-2xl shadow-xl border border-sage-100 p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-bold text-sage-800">${isSignup ? "Create account" : "Welcome back"}</h3>
          <button data-close class="text-sage-400 hover:text-sage-600 text-xl leading-none">&times;</button>
        </div>
        <form data-form class="space-y-3">
          ${
            isSignup
              ? `<label class="block">
                   <span class="text-xs text-sage-500">Name</span>
                   <input name="name" type="text" autocomplete="name"
                     class="mt-1 w-full rounded-lg border border-sage-200 focus:border-sage-500 focus:ring-2 focus:ring-sage-200 outline-none text-sm p-2" />
                 </label>`
              : ""
          }
          <label class="block">
            <span class="text-xs text-sage-500">Email</span>
            <input name="email" type="email" required autocomplete="email"
              class="mt-1 w-full rounded-lg border border-sage-200 focus:border-sage-500 focus:ring-2 focus:ring-sage-200 outline-none text-sm p-2" />
          </label>
          <label class="block">
            <span class="text-xs text-sage-500">Password</span>
            <input name="password" type="password" required minlength="8" autocomplete="${isSignup ? "new-password" : "current-password"}"
              class="mt-1 w-full rounded-lg border border-sage-200 focus:border-sage-500 focus:ring-2 focus:ring-sage-200 outline-none text-sm p-2" />
          </label>
          <p data-error class="text-xs text-red-600 min-h-[1rem]"></p>
          <button type="submit" data-submit
            class="w-full bg-sage-500 hover:bg-sage-600 text-white text-sm font-medium rounded-lg py-2.5 transition">
            ${isSignup ? "Create account" : "Sign in"}
          </button>
          ${
            !isSignup
              ? `<button type="button" data-forgot class="w-full text-center text-xs text-sage-500 hover:text-sage-700 underline pt-1">Forgot password?</button>`
              : ""
          }
        </form>
        <p class="text-xs text-sage-500 mt-4 text-center">
          ${isSignup ? "Already have an account?" : "New here?"}
          <button data-toggle class="text-sage-700 font-medium underline underline-offset-2">
            ${isSignup ? "Sign in" : "Create one"}
          </button>
        </p>
        <p class="text-[11px] text-sage-400 mt-3 text-center">
          You can keep browsing as a guest — accounts only add pins &amp; notes.
        </p>
      </div>`;

    overlayEl.querySelector("[data-close]").addEventListener("click", close);
    overlayEl.querySelector("[data-toggle]").addEventListener("click", () => {
      mode = isSignup ? "signin" : "signup";
      render();
    });

    const form = overlayEl.querySelector("[data-form]");
    const errEl = overlayEl.querySelector("[data-error]");
    const submitBtn = overlayEl.querySelector("[data-submit]");
    const forgotBtn = overlayEl.querySelector("[data-forgot]");
    if (forgotBtn) {
      forgotBtn.addEventListener("click", () => {
        mode = "reset";
        render();
      });
    }
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      errEl.textContent = "";
      submitBtn.disabled = true;
      submitBtn.textContent = "Please wait…";
      const fd = new FormData(form);
      try {
        if (isSignup) {
          await signup({
            email: fd.get("email"),
            password: fd.get("password"),
            name: fd.get("name"),
          });
        } else {
          await login(fd.get("email"), fd.get("password"));
        }
        close();
      } catch (err) {
        errEl.textContent =
          err?.message || "Something went wrong. Check your details and try again.";
        submitBtn.disabled = false;
        submitBtn.textContent = isSignup ? "Create account" : "Sign in";
      }
    });
  }

  // "Forgot password" — ask for an email, then confirm a link was sent. We never
  // reveal whether the email exists (PocketBase doesn't either).
  function renderReset() {
    overlayEl.innerHTML = `
      <div class="w-full max-w-sm bg-white rounded-2xl shadow-xl border border-sage-100 p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-bold text-sage-800">Reset your password</h3>
          <button data-close class="text-sage-400 hover:text-sage-600 text-xl leading-none">&times;</button>
        </div>
        <div data-reset-body>
          <p class="text-sm text-sage-600 mb-3">Enter your account email and we'll send you a link to set a new password.</p>
          <form data-reset-form class="space-y-3">
            <label class="block">
              <span class="text-xs text-sage-500">Email</span>
              <input name="email" type="email" required autocomplete="email"
                class="mt-1 w-full rounded-lg border border-sage-200 focus:border-sage-500 focus:ring-2 focus:ring-sage-200 outline-none text-sm p-2" />
            </label>
            <p data-error class="text-xs text-red-600 min-h-[1rem]"></p>
            <button type="submit" data-submit
              class="w-full bg-sage-500 hover:bg-sage-600 text-white text-sm font-medium rounded-lg py-2.5 transition">Send reset link</button>
          </form>
        </div>
        <p class="text-xs text-sage-500 mt-4 text-center">
          <button data-toggle class="text-sage-700 font-medium underline underline-offset-2">Back to sign in</button>
        </p>
      </div>`;

    overlayEl.querySelector("[data-close]").addEventListener("click", close);
    overlayEl.querySelector("[data-toggle]").addEventListener("click", () => {
      mode = "signin";
      render();
    });

    const rform = overlayEl.querySelector("[data-reset-form]");
    const rerr = overlayEl.querySelector("[data-error]");
    const rbtn = overlayEl.querySelector("[data-submit]");
    rform.addEventListener("submit", async (e) => {
      e.preventDefault();
      rerr.textContent = "";
      rbtn.disabled = true;
      rbtn.textContent = "Sending…";
      const email = String(new FormData(rform).get("email") || "");
      try {
        await requestPasswordReset(email);
      } catch {
        /* swallow — never reveal whether the account exists */
      }
      overlayEl.querySelector("[data-reset-body]").innerHTML = `
        <div class="text-center py-2">
          <p class="text-3xl mb-2">✉️</p>
          <p class="text-sm text-sage-700 font-medium">Check your email</p>
          <p class="text-xs text-sage-500 mt-1">If an account exists for <b>${escapeHtml(email)}</b>, a reset link is on its way. The link expires in 30 minutes.</p>
        </div>`;
    });
  }

  render();
  document.body.appendChild(overlayEl);
}

// Renders the top-bar auth control (Sign in link, or user + Sign out).
// Returns the element; re-renders itself on auth changes.
export function renderAuthControl() {
  const host = document.createElement("div");
  host.className = "flex items-center gap-3";

  function paint(state) {
    if (state.isAuthed) {
      const name = state.user?.name || state.user?.email || "Account";
      host.innerHTML = `
        <span class="text-xs text-sage-600 hidden sm:inline">${escapeHtml(name)}</span>
        <button data-logout class="text-xs font-medium text-sage-700 hover:text-sage-900 px-2.5 py-1 rounded-full border border-sage-200">
          Sign out
        </button>`;
      host.querySelector("[data-logout]").addEventListener("click", () => logout());
    } else {
      host.innerHTML = `
        <button data-signin class="text-xs font-medium text-sage-700 hover:text-sage-900 px-2.5 py-1 rounded-full border border-sage-200">
          Sign in
        </button>`;
      host.querySelector("[data-signin]").addEventListener("click", () => openAuthPanel("signin"));
    }
  }

  subscribe(paint);
  paint(getState());
  return host;
}
